const scrollIntoView = require('scroll-into-view-if-needed').default;
const map = require('./map');
const geolocation = require('../common/utils/geolocation');

const { FRONT_CONFIG } = window;

// @type {Object<storeId, marker>}
const locationsMap = {};

let selectedStoreId;
let selectedStore;

const storeLocatorContainerSelector = '.store-locator-grid';
let $container;
const elementSelectors = {
  $btnDeselectCountry: '.storelocator-deselect-country',
  $countryField: '#storelocator-country-selector-field',
  $postalCodeInput: '#store-postal-code',
  $countrySelect: '[name="country"]',
  $mapPopUp: '.storelocator-map-popup',
  $tabList: '[href="#store-locator-search-results"]',
  $tabMap: '[href="#storelocator-map"]',
  $searchResultsList: '#store-locator-search-results',
};
const elements = {};

/**
 * Caches the store locator container and resolves every selector in
 * `elementSelectors` into a jQuery element stored on the `elements` map.
 *
 * @returns {void}
 */
function queryStoreLocatorElements() {
  $container = $(storeLocatorContainerSelector);
  Object.keys(elementSelectors).forEach((key) => {
    elements[key] = $container.find(elementSelectors[key]);
  });
}

/**
 * appends params to a url
 * @param {string} url - Original url
 * @param {Object} params - Parameters to append
 * @returns {string} result url with appended parameters
 */
function appendToUrl(url, params) {
  let newUrl = url;
  newUrl +=
    (newUrl.indexOf('?') !== -1 ? '&' : '?') +
    Object.keys(params)
      .map((key) => `${key}=${encodeURIComponent(params[key])}`)
      .join('&');
  return newUrl;
}

const countrySelector = {
  hide: function hideCountrySelector() {
    const selectedCountry = elements.$countrySelect.val();
    if (selectedCountry?.length > 0) {
      elements.$countryField.hide();

      elements.$btnDeselectCountry.show();
    }
  },
  reset: function resetCountrySelector() {
    elements.$countrySelect[0].selectedIndex = 0;
    // As it changes programatically, change should be manually triggered
    elements.$countrySelect.change();

    elements.$countryField.show();
    elements.$btnDeselectCountry.hide();
  },
};

/**
 * Deselects a specific store location by its ID.
 * Updates the marker icon, hides the map popup, and removes selection from the list.
 *
 * @param {string} storeId - Unique identifier of the store to deselect
 * @returns {void}
 */
function deselectLocationById(storeId) {
  const location = locationsMap[storeId];
  const isUnavailable =
    location?.isCcCapacityFull || location?.isCcExpressOutOfStock;

  // Map
  map.updateMarkerIcon(storeId, false, isUnavailable);
  elements.$mapPopUp.html('');
  elements.$mapPopUp.hide();
  // List
  $(`#${storeId}`).removeClass('is-selected');
  if (selectedStoreId === storeId) {
    selectedStore = undefined;
    selectedStoreId = undefined;
  }
}

/**
 * Deselects a store location.
 * If a storeId is provided, deselects that specific store.
 * Otherwise, deselects all store locations.
 *
 * @param {string} [storeId=selectedStoreId] - Optional store ID to deselect
 * @returns {void}
 */
function deselectLocation(storeId = selectedStoreId) {
  if (storeId) {
    deselectLocationById(storeId);
  } else {
    Object.keys(locationsMap).forEach((id) => {
      deselectLocationById(id);
    });
  }
}

/**
 * Returns the store ID associated with a DOM element by walking up to
 * the closest `.result` and reading `data-store-id` from its `.store-details`.
 *
 * @param {HTMLElement|jQuery} element - DOM element inside a store result
 * @returns {string|undefined} Store ID or undefined if not found
 */
function getStoreId(element) {
  const $storeDetails = $(element).closest('.result').find('.store-details');
  return $storeDetails.data('store-id');
}

/**
 * Generates the HTML content for the map popup of a store location.
 *
 * @param {Object} mapLocationItem - Store location data
 * @param {string} mapLocationItem.id - Unique identifier of the store
 * @param {string} mapLocationItem.infoWindowHtml - Base HTML content for the popup
 * @param {boolean} mapLocationItem.isCcCapacityFull - Indicates if the store is unavailable due to capacity
 * @param {boolean} mapLocationItem.isCcExpressOutOfStock - Indicates if the store has no stock for any basket item (C&C Express)
 * @returns {string} HTML string for the map popup
 */
function getMapPopUpHtml(mapLocationItem) {
  if (!mapLocationItem) return '';

  const isDisabled =
    mapLocationItem.isCcCapacityFull || mapLocationItem.isCcExpressOutOfStock;

  return `${mapLocationItem.infoWindowHtml}
        <button
            class="btn btn-close store-details__btn-close"
            data-store="${mapLocationItem.id}"
        >
          <span class="visually-hidden">close</span>
        </button>
        <button
            class="btn btn-ok store-details__btn-ok
              ${isDisabled ? 'd-none' : ''}"
            data-store="${mapLocationItem.id}"
            ${isDisabled ? 'disabled=true' : ''}
        >
          ${FRONT_CONFIG.global.ok}
        </button>
    `;
}

/**
 * Selects a store location by its ID.
 * Updates marker state, centers the map if needed, displays the popup,
 * and marks the store as selected in the list.
 *
 * @param {string} storeId - Unique identifier of the store to select
 * @param {boolean} [confirm=false] - Whether the selection is confirmed (used in mobile flow)
 * @returns {void}
 */
function selectLocation(storeId, confirm = false) {
  const $listResult = $(`#${storeId}`);
  if (selectedStoreId !== storeId) {
    deselectLocation(); // Deselect previous location

    const location = locationsMap[storeId];

    // Map
    map.updateMarkerIcon(
      storeId,
      true,
      location?.isCcCapacityFull || location?.isCcExpressOutOfStock
    );
    if (!map.isMarkerVisible(storeId)) {
      map.centerMapInStore(storeId);
    }
    elements.$mapPopUp.html(getMapPopUpHtml(location));
    elements.$mapPopUp.show();
    // If using mobile map, wait for the ok button click
    const okButton = elements.$mapPopUp.find('.btn-ok');
    if (
      ($container.hasClass('map-show') || elements.$tabMap.is(':visible')) &&
      (okButton?.is(':visible') || okButton?.prop('disabled')) &&
      !confirm
    ) {
      return;
    }

    // List
    $listResult.addClass('is-selected');
    scrollIntoView($listResult[0], {
      block: 'start',
      behavior: 'smooth',
      scrollMode: 'if-needed',
      boundary: elements.$searchResultsList[0],
    });
    selectedStore = location;
    selectedStoreId = storeId;
  }

  $listResult
    .find('input.select-store-input')
    .prop('checked', true)
    .trigger('change');
}

/**
 * Indexes the given locations into `locationsMap`, keyed by store ID,
 * so they can be looked up later by `selectLocation`, `deselectLocationById`, etc.
 *
 * @param {Object[]} [locations] - Array of location objects from the server
 * @returns {void}
 */
function updateLocationsMap(locations = []) {
  if (!locations) return;
  locations.forEach((loc) => {
    locationsMap[loc.id] = loc;
  });
}

/**
 * Renders the results of the search and updates the map
 * @param {Object} data - Response from the server
 */
function updateStoresResults(data) {
  const $resultsDiv = $('.results');
  const $mapDiv = $('.map-canvas');
  const hasResults = data.stores.length > 0;

  if (!hasResults) {
    $('.store-locator-no-results').show();
  } else {
    $('.store-locator-no-results').hide();
  }
  $container.attr('data-has-results', hasResults);
  $('.store-locator__landing').attr('data-has-results', hasResults);

  $resultsDiv
    .empty()
    .data('has-results', hasResults)
    .data('radius', data.radius)
    .data('search-key', data.searchKey);

  updateLocationsMap(JSON.parse(data.locations));
  map.setLocationsData(data.locations);

  if ($mapDiv.data('has-google-api')) {
    map.renderMap(selectLocation, data.centerLocation);
  } else {
    $('.store-locator-no-apikey').show();
  }

  if (data.storesResultsHtml) {
    $resultsDiv.append(data.storesResultsHtml);
    addListenersForToggleStoreOpeningTime();
  }

  scrollIntoView($resultsDiv[0], {
    block: 'start',
    behavior: 'smooth',
    scrollMode: 'if-needed',
    boundary: elements.$searchResultsList[0],
  });
}

/**
 * Validates the store locator search form: the postal code / city field must
 * not be empty. When it is, marks the field as invalid and shows its missing
 * error message.
 * @param {string} [formSelector='.form.store-locator'] - Selector of the store
 *   locator form to validate.
 * @returns {boolean} True if the search field exists and has a value, false
 *   otherwise.
 */
function validFormFields(formSelector = '.form.store-locator') {
  const searchTermField = document.querySelector(
    `${formSelector} [name="postalCode"]`
  );
  if (!searchTermField) return false;
  resetFormFieldsValidation(formSelector);

  if (!searchTermField.value) {
    searchTermField.classList.add('is-invalid');
    const invalidFeedback =
      searchTermField.parentElement.querySelector('.invalid-feedback');
    if (invalidFeedback) {
      invalidFeedback.innerHTML = searchTermField.dataset.missingError;
    }
    return false;
  }
  return true;
}

/**
 * Clears the validation state of the store locator search field: removes the
 * invalid class and empties its error message.
 * @param {string} [formSelector='.form.store-locator'] - Selector of the store
 *   locator form to reset.
 */
function resetFormFieldsValidation(formSelector = '.form.store-locator') {
  const searchTermField = document.querySelector(
    `${formSelector} [name="postalCode"]`
  );
  if (!searchTermField) return;
  searchTermField.classList.remove('is-invalid');

  const invalidFeedback =
    searchTermField.parentElement.querySelector('.invalid-feedback');
  if (!invalidFeedback) return;
  invalidFeedback.innerHTML = '';
}

/**
 * Search for stores with new zip code
 * @param {HTMLElement} element - the target html element
 * @returns {boolean} false to prevent default event
 */
async function search(element) {
  if (!validFormFields()) return false;
  const dialog = element.closest('.in-store-inventory-dialog');
  const spinner = dialog.length ? dialog.spinner() : $.spinner();
  spinner.start();
  const $form = element.closest('.store-locator');
  const radius = $('.results').data('radius');
  const url = $form.attr('action');
  // var urlParams = { radius: radius };

  const apiKey = $form.find('[name="apiKey"]').val();
  const countryCodeFromSelect = elements.$countrySelect.val();
  const countryCodeFromSite = FRONT_CONFIG.countryCode;
  const countryCode = countryCodeFromSelect || countryCodeFromSite;

  const addressOrPostalCode = $form.find('[name="postalCode"]').val();

  const location = await map.geocodeAddress(
    addressOrPostalCode,
    countryCode,
    apiKey
  );

  let data;
  let payload;
  if (location !== null) {
    payload = {
      lat: location.lat,
      long: location.lng,
      countryCode,
    };

    // Disable radius filtering, always search from 10km until finding stores
    // url = appendToUrl(url, urlParams);

    data = await $.ajax({
      url,
      type: $form.attr('method'),
      data: payload,
      dataType: 'json',
    });

    data.centerLocation = location;
  } else {
    // Data should be an empty search results and centered on country
    data = {
      centerLocation: await map.geocodeAddress('', countryCode, apiKey),
      locations: '[]',
      radius,
      searchKey: payload,
      stores: [],
    };
  }

  spinner.stop();
  // countrySelector.hide();
  updateStoresResults(data);
  $('body').trigger('store-locator:search-update');

  return false;
}

/* -------------------------------------------------------------------------- *\
 *    Event handlers                                                          *
\* -------------------------------------------------------------------------- */
/**
 * Handles a change of the search radius select: repeats the last search
 * (by postal code or by coordinates) with the new radius and updates the
 * results list. Bound by jQuery, so `this` is the radius select.
 */
function handleRadiusChange() {
  const target = $(this);
  deselectLocation();
  const radius = $(target).val();
  const searchKeys = $('.results').data('search-key');
  let url = $(target).data('action-url');
  let urlParams = {};

  if (searchKeys.postalCode) {
    urlParams = {
      radius,
      postalCode: searchKeys.postalCode,
    };
  } else if (searchKeys.lat && searchKeys.long) {
    urlParams = {
      radius,
      lat: searchKeys.lat,
      long: searchKeys.long,
    };
  }

  url = appendToUrl(url, urlParams);
  const dialog = $(target).closest('.in-store-inventory-dialog');
  const spinner = dialog.length ? dialog.spinner() : $.spinner();
  spinner.start();
  $.ajax({
    url,
    type: 'get',
    dataType: 'json',
    success(data) {
      spinner.stop();
      updateStoresResults(data);
      $('.select-store').prop('disabled', true);
    },
  });
}

/**
 * Handles the events that launch a store search (form submit, search button
 * click, Enter key): prevents the default action, clears the selected location
 * and searches with the current form values.
 * @param {Event} event - The submit, click or keydown event.
 */
function handleEventsToSearch(event) {
  event.preventDefault();
  deselectLocation();
  search($(event.target));
}

/**
 * Checks the geolocation permission on eligible pages and, unless it is
 * denied, enables the "detect location" button and wires its click handler to
 * query nearby stores based on the user's current coordinates. The position is
 * only requested on click: asking for it on page load opens the browser prompt
 * on every checkout, which blocks the native selects of the address form.
 */
function addListenersForDetectingLocation() {
  /**
   * Fetches stores near the given coordinates and updates the results list.
   * @param {geolocation.Coordinates} coords - User's current coordinates.
   */
  const queryGeolocationStores = (coords) => {
    const $detectLocationButton = $('.detect-location');
    let url = $detectLocationButton.data('action');
    const radius = $('.results').data('radius');
    const urlParams = {
      radius,
      lat: coords.lat,
      long: coords.lng,
    };

    url = appendToUrl(url, urlParams);
    $.ajax({
      url,
      type: 'get',
      dataType: 'json',
      success(data) {
        $.spinner().stop();
        updateStoresResults(data);
        $('body').trigger('store-locator:search-update');
        $('.select-store').prop('disabled', true);
      },
    });
  };

  const enableDetectLocation = () => {
    $('.detect-location').prop('disabled', false);

    $('.detect-location').on('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      $.spinner().start();
      resetFormFieldsValidation();

      geolocation
        .getCurrentPositionAsync()
        .then(queryGeolocationStores)
        .catch(() => $.spinner().stop());
    });
  };

  const page = document.querySelector('.page');
  const isGeolocationPage =
    page?.dataset.action === 'Stores-Find' ||
    page?.dataset.action === 'Account-Orders';

  if (isGeolocationPage || FRONT_CONFIG.isAnyPickupEnabled) {
    geolocation.getPermissionStateAsync().then((state) => {
      if (state === 'granted' || state === 'prompt') enableDetectLocation();
    });
  }
}

/**
 * Wires the store search triggers: the form submit, the search button click
 * and the Enter key in the postal code field. Listeners are removed before
 * being added so repeated inits do not duplicate them.
 */
function addListenersForSearching() {
  const storeLocatorForm = document.querySelector(
    `${storeLocatorContainerSelector} .form.store-locator`
  );
  if (storeLocatorForm) {
    storeLocatorForm.removeEventListener('submit', handleEventsToSearch);
    storeLocatorForm.addEventListener('submit', handleEventsToSearch);
  }
  const searchButton = document.querySelector(
    `${storeLocatorContainerSelector} .btn-storelocator-search[type="button"]`
  );
  if (searchButton) {
    searchButton.removeEventListener('click', handleEventsToSearch);
    searchButton.addEventListener('click', handleEventsToSearch);
  }

  /**
   * Launches the search when Enter is pressed in the postal code field.
   * @param {KeyboardEvent} event - The keydown event.
   */
  function handleEnterKeyDown(event) {
    if (event.keyCode === 13) {
      handleEventsToSearch(event);
    }
  }

  elements.$postalCodeInput.on('keydown', handleEnterKeyDown);
}

/**
 * Wires click handlers for selecting a store from the results list.
 */
function addListenersForSelectingStores() {
  $(storeLocatorContainerSelector)
    .off('click', '.js-store-select')
    .on('click', '.js-store-select', (e) => {
      const storeId = getStoreId(e.target);
      if (locationsMap[storeId]?.isCcExpressOutOfStock) return;
      selectLocation(storeId);
    });

  $(storeLocatorContainerSelector)
    .off('click', '.getStoreId')
    .on('click', '.getStoreId', (e) => {
      e.preventDefault();
      const selectedResult = $(':checked', '.results-card .results');
      const data = {
        storeID: selectedResult.val(),
        searchRadius: $('#radius').val(),
        searchPostalCode: $('.results').data('search-key').postalCode,
        storeDetailsHtml: selectedResult
          .siblings('label')
          .find('.store-details')
          .html(),
        event: e,
      };

      $('body').trigger('store:selected', data);
    });
}

/**
 * Handles the map tab being hidden: removes the map display state from the
 * store locator container.
 */
function handleMapTabHide() {
  $container.removeClass('map-show');
}

/**
 * Handles the map tab being shown: marks the container as showing the map and
 * fits the map bounds to the current locations.
 */
function handleMapTabShown() {
  $container.addClass('map-show');
  map.fitBoundsOnLocations();
}

/**
 * Wires the open/close toggle of the store opening times and closes any that
 * were left open.
 * @param {string} [parentSelector] - Selector of the container holding the
 *   stores; defaults to the store locator container.
 */
function addListenersForToggleStoreOpeningTime(parentSelector) {
  const storeOpeningTimesClassName = 'store-opening-times';
  const storeOpeningTimesOpenClassName = `${storeOpeningTimesClassName}--open`;
  const storeOpeningTimesSelector = `.${storeOpeningTimesClassName}`;
  const storeOpeningTimesOpenBtnOpen = `${storeOpeningTimesSelector}__toggler--open`;

  const selector = $(parentSelector || storeLocatorContainerSelector);

  selector
    .off('click', storeOpeningTimesSelector)
    .on('click', storeOpeningTimesSelector, (e) => {
      e.preventDefault();
      e.stopPropagation();
      const storeOpeningTimesContainer = e.target?.closest(
        storeOpeningTimesSelector
      );
      if (e.target?.matches(storeOpeningTimesOpenBtnOpen)) {
        storeOpeningTimesContainer?.classList.add(
          storeOpeningTimesOpenClassName
        );
        return;
      }
      storeOpeningTimesContainer?.classList.remove(
        storeOpeningTimesOpenClassName
      );
    });

  // Reset state of elements
  selector
    .find(`.${storeOpeningTimesOpenClassName}`)
    .removeClass(storeOpeningTimesOpenClassName);
}

module.exports = {
  init() {
    map.init();

    selectedStoreId = undefined;
    selectedStore = undefined;

    queryStoreLocatorElements($(storeLocatorContainerSelector));

    if ($('.map-canvas').data('has-google-api')) {
      updateLocationsMap(map.getLocationsData());
      map.renderMap(selectLocation);
    } else {
      $('.store-locator-no-apikey').show();
    }

    // Hide back button on init
    elements.$btnDeselectCountry?.hide();

    addListenersForDetectingLocation();
    addListenersForSearching();
    addListenersForSelectingStores();
    addListenersForToggleStoreOpeningTime();

    // Radius change (via select element)
    $(`${storeLocatorContainerSelector} .radius`)
      .off('change', handleRadiusChange)
      .on('change', handleRadiusChange);

    // updateSelectStoreButton
    $('body')
      .off('change', '.getStoreId-input')
      .on('change', '.getStoreId-input', () => {
        $('.select-store').prop('disabled', false);
      });

    // On click: reset country and show select
    elements.$btnDeselectCountry?.off('click').on('click', (e) => {
      e.preventDefault();
      countrySelector.reset();
    });

    elements.$countrySelect?.off('change').on('change', () => {
      elements.$postalCodeInput[0].value = '';
    });

    elements.$mapPopUp
      ?.off('click', '.btn-close')
      .on('click', '.btn-close', (e) => {
        deselectLocation($(e.target).data('store'));
      });

    elements.$mapPopUp?.off('click', '.btn-ok').on('click', '.btn-ok', (e) => {
      e.preventDefault();
      selectLocation($(e.target).data('store'), true);
    });

    elements.$tabMap[0]?.removeEventListener('byl:tabs.hide', handleMapTabHide);
    elements.$tabMap[0]?.addEventListener('byl:tabs.hide', handleMapTabHide);

    elements.$tabMap[0]?.removeEventListener(
      'byl:tabs.shown',
      handleMapTabShown
    );
    elements.$tabMap[0]?.addEventListener('byl:tabs.shown', handleMapTabShown);
  },
  selectStore(storeId) {
    selectLocation(storeId);
  },
  getSelectedStore() {
    return selectedStore;
  },
  validFormFields,
  resetFormFieldsValidation,
  addListenersForToggleStoreOpeningTime,
};
